/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/qaTabSelectors.ts
 * FILE_ID: JOBHUB-QA-TAB-SELECTORS-001
 * PURPOSE: Pure display and status-transition selectors for the Job QA tab.
 */
import type { QaArea, QaFloor, QaItemStatus } from '../../qa/qaApi'

const STATUS_CYCLE: QaItemStatus[] = ['not_started', 'done', 'further_action', 'failed', 'not_applicable']

/**
 * The rail's first two levels: each Level with its Locations.
 *
 * Levels come from their own query rather than being derived from the areas,
 * so a Level the user emptied by moving its last Location away still appears
 * and can receive one back. Any area whose Level is missing falls into a
 * trailing unfiled group — losing a Location off the rail would hide real QA
 * work behind a stale nested list.
 */
export function levelsOf(
  floors: QaFloor[],
  areas: QaArea[],
): { floor: QaFloor | null; areas: QaArea[] }[] {
  // Boundary defense: callers hand these straight from API payloads, and a
  // partially-loaded or error-shaped response (undefined, an object with
  // `detail`) crashed the legacy apps/qa record page with `.map is not a
  // function` (operator report 2026-09-15). Coerce, never throw.
  const safeFloors = Array.isArray(floors) ? floors : []
  const safeAreas = Array.isArray(areas) ? areas : []
  const placed = new Set<string>()
  const levels = safeFloors.map((floor) => {
    // The areas query is the authority on an area's fields, so match the
    // nested ids back onto it rather than rendering two different snapshots.
    const ids = new Set((floor.areas ?? []).map((area) => area.id))
    const own = safeAreas.filter((area) => ids.has(area.id) || area.floor === floor.id)
    own.forEach((area) => placed.add(area.id))
    return { floor, areas: own }
  })
  const orphans = safeAreas.filter((area) => !placed.has(area.id))
  return orphans.length ? [...levels, { floor: null, areas: orphans }] : levels
}

export function groupNameOf(area: { floor?: string | null }, floors: readonly QaFloor[]): string {
  const ordered = [...floors].sort((a, b) => (a.sort_order - b.sort_order) || a.floor_name.localeCompare(b.floor_name))
  const home = ordered[0]
  if (!area.floor || !home || area.floor === home.id) return ''
  return ordered.find((floor) => floor.id === area.floor)?.floor_name ?? ''
}

export const QA_PENDING_DELETE_NOTICE =
  'Delete sent to the job owner for approval. The check stays until it is approved.'

export function isPendingQaChange(result: unknown): boolean {
  if (!result || typeof result !== 'object') return false
  const row = result as { status?: string; pending_change_id?: unknown; kind?: string }
  if (row.status === 'pending') return true
  return typeof row.pending_change_id === 'string' && row.pending_change_id.length > 0
    && (row.kind === 'delete' || row.kind === 'edit')
}

export function qaErrorMessage(error: unknown, fallback: string): string {
  return (
    (error as { response?: { data?: { error?: string; detail?: string } } })?.response?.data?.error ||
    (error as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
    (error as Error)?.message ||
    fallback
  )
}

/**
 * Next state in the quick-tap cycle, unchanged for the four states it covers.
 *
 * A status outside the cycle (in_progress, further_action, both only reachable
 * through the explicit picker) has no successor here, so it restarts the cycle
 * at not_started rather than landing on an arbitrary state. indexOf returning
 * -1 already produced exactly that; it is now deliberate and pinned.
 */
export function nextQaItemStatus(current: QaItemStatus): QaItemStatus {
  const idx = STATUS_CYCLE.indexOf(current)
  const from = idx < 0 ? 0 : idx
  return STATUS_CYCLE[(from + 1) % STATUS_CYCLE.length]
}

/** Newest inspection that already has floors; falls back to the newest. A new
 *  empty inspection must never hide the one the crew is working in. */
export function pickDefaultInspection<T extends { id: string; floor_count?: number }>(
  rows: readonly T[],
): T | null {
  return rows.find((row) => (row.floor_count ?? 0) > 0) ?? rows[0] ?? null
}
